import clsx from "clsx";
import { Tag, TagColor } from "react-migration/components/Tag";
import { DataPanel } from "react-migration/components/DataPanel";
import { protectedSmallDateFormatter } from "../../utils/protectedDateFormatter";
import { usePlanningTypesDisplay } from "../../utils/usePlanningTypesDisplay";
import { PlanningApplication } from "../../types";
import { getStatusTagColor } from "../../utils/colors";
import { ClassificationBadge } from "../ClassificationBadge";
import { AppealTag } from "../AppealTag";

export interface PlanningApplicationDetailMiniCardProps {
  onTitleClick?: (application: PlanningApplication) => void;
  application: PlanningApplication;
  printable?: boolean;
}

export function PlanningApplicationDetailMiniCard({
  onTitleClick,
  application,
  printable = false,
}: PlanningApplicationDetailMiniCardProps) {
  const { ref, title, classification, address, status_derived, types_derived, date_received, appeal_decision } =
    application;

  const statusTagColor = getStatusTagColor(status_derived) || TagColor.DEFAULT;
  const typesDisplay = usePlanningTypesDisplay(types_derived);
  const dateDisplay = protectedSmallDateFormatter(date_received);

  return (
    <article
      className={clsx("bg-white border border-border-action rounded-xl p-4 flex flex-col cursor-default", {
        "print:break-inside-avoid min-w-full": printable,
      })}
      data-testid="planning-application-detail-mini-card"
    >
      <div className="flex flex-row gap-x-2">
        <ClassificationBadge classification={classification} />
        <h1
          onClick={() => onTitleClick?.(application)}
          className={clsx("text-sm font-medium text-text-base-active truncate", {
            "hover:text-text-base-active-hover cursor-pointer": !!onTitleClick,
          })}
        >
          {ref}
        </h1>
        {appeal_decision && (
          <div>
            <AppealTag appealDecision={appeal_decision} />
          </div>
        )}
      </div>
      <div className="mt-1">
        <p
          title={title}
          className={clsx("text-sm font-normal text-black h-auto whitespace-normal", {
            "line-clamp-2 h-10": !printable,
          })}
        >
          {title}
        </p>
        <p title={address} className="text-xs font-normal text-text-base-secondary-a line-clamp-1">
          {address}
        </p>
      </div>
      <DataPanel.Grid columns={3}>
        <DataPanel.Cell title="Status">
          <Tag color={statusTagColor} title={`status: ${status_derived}`} tag={status_derived} />
        </DataPanel.Cell>
        <DataPanel.Cell title="Type">
          <span title={`types: ${typesDisplay}`} className="line-clamp-1 text-sm">
            {typesDisplay}
          </span>
        </DataPanel.Cell>
        <DataPanel.Cell title="Received">
          <span title={`date received: ${dateDisplay}`} className="line-clamp-1 text-sm">
            {dateDisplay}
          </span>
        </DataPanel.Cell>
      </DataPanel.Grid>
    </article>
  );
}
